import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { toast } from 'react-toastify';
import convertingIllustration from '../../../static/icons/convertible-file.svg';
import Toast from '../../Toast';
import { BulkUploadWizardShell } from './BulkUploadWizardShell';
import { submissionBatchErrorMessage } from '../utils/submissionBatchErrors';
import { ProgressAction } from '../styles';

const INITIAL_PROGRESS = 8;
const STARTED_PROGRESS = 20;

/**
 * Waiting for text to come back, after the fan-out moved server-side.
 *
 * This step no longer starts N requests, polls them, or gives up on them: one
 * POST asks for the whole assignment (useAssignmentExtractionRun, owned by the
 * flow) and react-query does the watching. What is left here is the decision of
 * when to move on, and a bar while it has not been made.
 *
 * Review opens after every response in this batch has finished conversion.
 * The flow owns polling so closing and reopening the wizard can resume progress.
 */
export const BulkUploadConvertingStep = ({
  steps,
  currentStep = 3,
  pages = [],
  assignmentId,
  students = [],
  extraction,
  submissionIds,
  isCommitting = false,
  commitError,
  onRetryCommit,
  onComplete,
  onBack,
  onClose,
  onStepClick,
  fullPage = false,
}) => {
  const completedRef = useRef(false);
  const startedRef = useRef(false);
  /**
   * A read has landed that post-dates the kick.
   *
   * Before the kick every row is quiet because nothing has been asked for; after
   * it, a quiet row means the run had nothing to do for that student. The two are
   * indistinguishable in the status body, so "settled" is only allowed to end
   * this step once we know which side of the kick the reading came from.
   */
  const [hasReadSinceKick, setHasReadSinceKick] = useState(false);
  const [startError, setStartError] = useState(null);
  const [attempt, setAttempt] = useState(0);

  // The flow rebuilds this every render and a stale one would finish the wrong
  // wizard, so it alone is mirrored.
  const onCompleteRef = useRef();
  onCompleteRef.current = onComplete;

  const finish = useFinishOnce(completedRef, onCompleteRef);

  useStartExtraction({
    assignmentId,
    extraction,
    startedRef,
    students,
    finish,
    isCommitting,
    submissionIds,
    attempt,
    onFailure: setStartError,
    onRead: () => setHasReadSinceKick(true),
  });
  useAdvanceWhenReadable({ extraction, finish, hasReadSinceKick: hasReadSinceKick && !isCommitting });

  const progress = progressOf(extraction, completedRef.current);

  return (
    <BulkUploadWizardShell
      steps={steps}
      currentStep={currentStep}
      onStepClick={onStepClick}
      onBack={onBack}
      onClose={onClose}
      // Not hideFooter: this step decides for itself when to move on, so it has
      // no Continue — but a teacher who spots a mis-routed page while the class
      // converts still needs the way back to Organise.
      hideContinue
      fullPage={fullPage}
      data-component="bulk-upload-converting"
      data-unit="bulk-upload-step-2"
      data-total-pages={pages.length}
      data-progress={progress}
      data-is-complete={completedRef.current}
      data-extracted={extraction?.extracted ?? 0}
    >
      <Panel>
        <Illustration
          src={convertingIllustration}
          alt=""
          width={420}
          height={310}
        />
        <Track aria-hidden="true">
          <Fill $value={progress} />
        </Track>
        <Title>{isCommitting ? 'Saving your responses…' : 'Converting files to editable text…'}</Title>
        {commitError ? <>
          <p role="alert">We could not confirm that your responses were saved. Retry to continue the same upload.</p>
          <ProgressAction type="button" onClick={onRetryCommit}>Try again</ProgressAction>
        </> : startError ? (
          <>
            <p role="alert">{submissionBatchErrorMessage(startError, students)}</p>
            <ProgressAction type="button" onClick={() => {
              setStartError(null);
              setHasReadSinceKick(false);
              startedRef.current = false;
              setAttempt((value) => value + 1);
            }}>Try again</ProgressAction>
          </>
        ) : <p role="status">{isCommitting ? 'Preparing the batch for conversion.' : extraction?.message}</p>}
      </Panel>
    </BulkUploadWizardShell>
  );
};

/** Leaving is a one-way door: a later poll tick must not fire it again. */
const useFinishOnce = (completedRef, onCompleteRef) =>
  React.useCallback(() => {
    if (completedRef.current) return;
    completedRef.current = true;
    onCompleteRef.current?.();
  }, [completedRef, onCompleteRef]);

/**
 * One request for the whole assignment, once.
 *
 * A draft with no assignment routed nothing to anybody, so there is no submission
 * to extract and nothing to wait for — that is a finished step, not a failure.
 */
const useStartExtraction = ({
  assignmentId,
  extraction,
  startedRef,
  students,
  finish,
  onRead,
  isCommitting,
  submissionIds,
  attempt,
  onFailure,
}) => {
  useEffect(() => {
    if (isCommitting || startedRef.current) return;
    startedRef.current = true;
    if (!assignmentId) {
      finish();
      return;
    }
    // The read is asked for rather than waited on: the mutation invalidates the
    // query too, but this step needs to KNOW a post-kick reading has landed
    // before it is allowed to read quiet rows as "nothing left to do".
    const kick = async () => {
      await extraction?.start?.(submissionIds?.length ? { submissionIds } : undefined);
      await extraction?.refetchStatus?.();
      onRead();
    };
    // Extraction is a preview: if the kick is refused the teacher can still type
    // the text in at Review, so we say why and move on rather than block.
    kick().catch((error) => {
      toast(<Toast message={submissionBatchErrorMessage(error, students)} />);
      onFailure(error);
    });
    // Mount only — the run is started once and watched by the flow's poll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCommitting, attempt]);
};

/**
 * The first student to extract is enough to open Review on: they have real text
 * to show, and everyone behind them fills in as the poll lands.
 *
 * A run with nothing left in flight also moves on, whether that is because every
 * response failed or because the run had nothing to do — a class of typed
 * responses is converted the moment it is asked for. Holding the teacher on a bar
 * that can never fill is the failure this whole change replaces, so that case
 * needs an exit as much as the slow one does.
 */
const useAdvanceWhenReadable = ({ extraction, finish, hasReadSinceKick }) => {
  const quiet = hasReadSinceKick && Boolean(extraction?.isSettled);
  useEffect(() => {
    if (quiet) finish();
  }, [quiet, finish]);
};

/**
 * The bar reads the same rows the decision does. It is not a share of the class:
 * we leave at the first success, so a bar that only reached 1/30 before advancing
 * would read as a failure. Anything readable is the whole of this step's job.
 */
const progressOf = (extraction, isComplete) => {
  if (isComplete) return 100;
  if (!extraction?.status) return INITIAL_PROGRESS;
  const rows = extraction.status.submissions || [];
  const settled = rows.filter((row) => ['EXTRACTED', 'FAILED', 'SKIPPED'].includes(row.extraction?.status)
    || (!row.extraction?.status && row.extraction?.text)).length;
  if (rows.length) return Math.min(95, Math.max(STARTED_PROGRESS, Math.round(settled / rows.length * 100)));
  return STARTED_PROGRESS;
};

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
`;

const Panel = styled.div`
  flex: 1;
  align-self: stretch;
  width: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  box-sizing: border-box;
  text-align: center;
  gap: 24px;
`;

const Illustration = styled.img`
  width: min(420px, 90%);
  height: auto;
  max-height: 310px;
  object-fit: contain;
  display: block;
  animation: ${float} 2.8s ease-in-out infinite;
`;

const Track = styled.div`
  width: min(100%, 280px);
  height: 6px;
  border-radius: 999px;
  background: #e8e6eb;
  overflow: hidden;
`;

const Fill = styled.div`
  height: 100%;
  width: ${({ $value }) => $value}%;
  background: #7200e0;
  transition: width 0.2s ease;
`;

const Title = styled.h2`
  margin: 0;
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 20px;
  font-weight: 600;
  color: #1e252a;
`;
